import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GamificationConfig } from '@troia-v3/types';

import { usePermissions } from '@/hooks/usePermissions';
import { Breadcrumb, Button, ListPageEmpty, ListPageLoading, PageToolbar, Tabs } from '@/shared/components/ui';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';

import { AuditTab } from '../components/AuditTab';
import { ConfigTab, type ConfigDraft } from '../components/ConfigTab';
import { levelsError } from '../components/LevelsEditor';
import { rulesError } from '../components/PointsRulesTable';
import { useGamificationConfig, useGamificationEventsSummary, useUpdateGamificationConfig } from '../hooks/useGamification';

import '../gamification.css';

const pickDraft = (c: GamificationConfig): ConfigDraft => ({
  enabled: c.enabled, rules: c.rules.map((r) => ({ ...r })), levels: c.levels.map((l) => ({ ...l })),
  period: c.period, rankings: [...c.rankings], visibility: c.visibility, celebrate: { ...c.celebrate },
});

/**
 * Configurações › Gamificação (mockup lote-b/10). Toolbar 3 zonas: breadcrumb |
 * vazio | Descartar + Salvar alterações. Abas coladas ao header (`Tabs pageLevel`).
 * Um rascunho (`draft`) para as 5 seções; um PUT parcial no salvar.
 */
export const GamificationSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { hasModulePermission } = usePermissions();
  const canRead = hasModulePermission('gamification', 'read');
  const canUpdate = hasModulePermission('gamification', 'update');

  const { data: config, isLoading, error } = useGamificationConfig(canRead);
  const { data: summary } = useGamificationEventsSummary(undefined, canRead);
  const update = useUpdateGamificationConfig();

  const [tab, setTab] = useState<'config' | 'audit'>('config');
  const [draft, setDraft] = useState<ConfigDraft | null>(null);

  useEffect(() => { if (config) setDraft(pickDraft(config)); }, [config]);

  const dirty = useMemo(() => !!config && !!draft && JSON.stringify(pickDraft(config)) !== JSON.stringify(draft), [config, draft]);
  const invalid = useMemo(
    () => (draft ? levelsError(draft.levels) !== null || rulesError(draft.rules) !== null || draft.rankings.length === 0 : true),
    [draft],
  );

  const onSave = async () => {
    if (!draft || !config) return;
    const saved = pickDraft(config);
    const patch: Partial<ConfigDraft> = {};
    (Object.keys(draft) as Array<keyof ConfigDraft>).forEach((k) => {
      if (JSON.stringify(draft[k]) !== JSON.stringify(saved[k])) (patch as Record<string, unknown>)[k] = draft[k];
    });
    await update.mutateAsync(patch);
  };

  const onDiscard = async () => {
    if (!config) return;
    if (dirty && !(await confirm({ message: 'Descartar as alterações não salvas?', confirmButtonText: 'Descartar', variant: 'danger' }))) return;
    setDraft(pickDraft(config));
  };

  if (!canRead) {
    return (
      <div className="h-full flex items-center justify-center px-6" data-testid="gamification-denied">
        <ListPageEmpty icon="award" message="Sem acesso à Gamificação" description="Peça a um administrador a permissão de leitura do módulo Gamificação." />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }} data-testid="gamification-page">
      <PageToolbar
        data-testid="gamification-toolbar"
        left={<Breadcrumb items={[{ label: 'Configurações', onNavigate: () => navigate('/settings'), testid: 'gamification-crumb-settings' }, { label: 'Gamificação', testid: 'gamification-crumb-current' }]} mobileLegend="Configurações" />}
        right={tab === 'config' ? (
          <>
            <Button variant="secondary" onClick={() => { void onDiscard(); }} disabled={!dirty || update.isPending} data-testid="gamification-discard">Descartar</Button>
            {canUpdate && (
              <Button variant="primary" icon="check" collapsibleLabel onClick={() => { void onSave(); }} disabled={!dirty || invalid} loading={update.isPending} data-testid="gamification-save">Salvar alterações</Button>
            )}
          </>
        ) : undefined}
      />

      <Tabs
        variant="underline"
        pageLevel
        hideIconsOnMobile
        activeTab={tab}
        onChange={(id) => setTab(id as 'config' | 'audit')}
        tabs={[
          {
            id: 'config', label: 'Configuração', icon: 'settings', 'data-testid': 'gamification-tab-config',
            content: isLoading || !draft
              ? <ListPageLoading message="Carregando configuração..." />
              : error
                ? <div className="p-6" style={{ color: 'var(--theme-error-fg)' }} data-testid="gamification-error">Erro ao carregar: {error.message}</div>
                : <ConfigTab draft={draft} onChange={(p) => setDraft((d) => (d ? { ...d, ...p } : d))} disabled={!canUpdate} {...(summary ? { summary } : {})} />,
          },
          {
            id: 'audit', label: 'Auditoria de pontos', icon: 'list', 'data-testid': 'gamification-tab-audit',
            ...(summary && summary.pendingCount > 0 ? { badge: summary.pendingCount } : {}),
            content: <AuditTab {...(config ? { period: config.period } : {})} />,
          },
        ]}
      />
    </div>
  );
};
GamificationSettingsPage.displayName = 'GamificationSettingsPage';
